/**
 * Inline composer for tenant_admin to ask the customer a clarifying
 * question. Submitting transitions the estimate to INFO_REQUESTED
 * (idempotent on already-INFO_REQUESTED) and posts the question as a
 * flagged assistant turn in chat.
 */

import { useState } from "react";

import { askClarification } from "@starz/shared/api/sdk.gen";
import type { Estimate } from "@starz/shared/api/types.gen";

interface Props {
  estimate: Estimate;
  onAsked: (updated: Estimate) => void;
}

const MAX_LEN = 1000;

export function ClarificationComposer({ estimate, onAsked }: Props) {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSend() {
    const q = question.trim();
    if (!q) {
      setError("Question is required.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await askClarification({
      estimateId: estimate.estimate_id,
      body: { question: q },
    });
    setBusy(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setQuestion("");
    onAsked(result.data);
  }

  return (
    <div className="space-y-3 rounded-lg border border-starz-gold/30 bg-starz-cream/40 p-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Ask the customer a question
      </p>
      <textarea
        rows={3}
        maxLength={MAX_LEN}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="e.g. Is the drainage line on the south or east side?"
        className="w-full rounded-lg border border-starz-gold/30 bg-white p-3 text-sm shadow-inner focus:border-starz-gold focus:outline-none"
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-warm-gray">
          {question.length}/{MAX_LEN}
        </span>
        <button
          type="button"
          onClick={onSend}
          disabled={busy || question.trim().length === 0}
          className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-2 text-sm font-semibold uppercase tracking-wide text-starz-off-white disabled:opacity-60"
        >
          {busy ? "Sending…" : "Send to customer"}
        </button>
      </div>
      {error && (
        <p className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
    </div>
  );
}
